import type { ManageAPI } from "~/manage-api/types";

const NUMBER = "#";

const RANGES = [
  [48, 57],   // 0-9
  [65, 90],   // A-Z
  [97, 122],  // a-z
  [192, 214], // À-Ö
  [216, 246], // Ø-ö
];

const PROTO_REGEX = /^(https?|email|tel):(\/\/)?/;

function isInRanges (code: number): number {
  for (let index = 0; index < RANGES.length; index++) {
    const [L,U] = RANGES[index];
    if (code >= L && code <= U) {
      return index
    }
  }
  return -1;
}

function getInitial (name: string = "") {
  for (const c of name) {
    const range = isInRanges(c.charCodeAt(0));
    if (range == 0)  return NUMBER;
    if (range > 0) return c.toUpperCase();
  }
  // Shouldn't happen with sane names
  return NUMBER;
};

export type GroupOfStores = {
  name: string;
  stores: ManageAPI.Store[]
}

/**
 * Groups stores by the initial of the store name. An intial is first number or alphabetic 
 * character.
 * @param stores 
 * @param locale 
 * @returns 
 */
export function groupByInitial(stores: ManageAPI.Store[], locale:string): GroupOfStores[] {

  const group_map = stores.reduce((acc: Map<string, GroupOfStores>, store) => {
      const initial = getInitial(store.name);
      const group = acc.get(initial) || { name: initial, stores: [] };
      if (!group.stores.length) acc.set(initial, group);
      group.stores.push(store);
      return acc;
  }, new Map());

  return [...group_map.values()].sort((a, b) =>
    a.name.localeCompare(b.name, locale, {
      numeric: true,
      sensitivity: "base",
    })
  );
}

export const offerBelongToCustomFob = (offers: readonly ManageAPI.Offer[], stores: ManageAPI.Store[], grouping: Grouping ): ManageAPI.Offer[] => {
  let storesFound = filterByGrouping(stores, grouping )
  let storesIDs = storesFound?.map(store => store?.id);
  return offers.filter((offer) => offer?.store?.id && storesIDs.includes(offer.store.id));
}

export const belongsToFob = (stores: ManageAPI.Store[], fobs: number[], fobsConfig:[{id:number, fob_ids: number[], description:string}]) => {
  let fobsIDS: number[] = [];
  
  fobs.forEach((fob)=> {
    fobsConfig.filter(obj => {
      if(obj.id == fob ){
        obj.fob_ids.forEach(i => fobsIDS.push(i))
        }
    })
  })
  return stores.reduce((acc: ManageAPI.Store[], store: ManageAPI.Store) => {
    let _fob = store.additionalFieldOfBusinessIds? store.additionalFieldOfBusinessIds.concat(store.fieldOfBusinessId): [store.fieldOfBusinessId];
    const found = _fob.some(r=> fobsIDS.includes(r))
    if (found) acc.push(store);
    return acc;
  }, []);
};
export const filterByGrouping = (stores: ManageAPI.Store[], grouping: Grouping): ManageAPI.Store[] => {

  // Return all stores if ids array contains 0
  if (grouping.ids.includes(0)) return stores;
  
  if (grouping.type == 'lineOfBusiness') {
    return stores.reduce((acc: ManageAPI.Store[], store: ManageAPI.Store) => {
      let _fob = store.additionalFieldOfBusinessIds? store.additionalFieldOfBusinessIds.concat(store.fieldOfBusinessId): [store.fieldOfBusinessId];
      const found = _fob.some(r=> grouping.ids.includes(r))
      if (found) acc.push(store);
      return acc;
    }, []);
  } else  {
    return stores.reduce((acc: ManageAPI.Store[], store: ManageAPI.Store) => {
      if (grouping.ids.includes(store.id)) acc.push(store);
      return acc; 
    }, []);
  }
}
export function findFobByQuery(query:string, fobs:ManageAPI.Fob[] | Grouping[]) {
  let foundFob;
  let fobNumber=parseInt(query);
  if(isNaN(fobNumber)){
    foundFob = fobs.find(o => o.name === query);
  } else  {
    foundFob = fobs.find(o => o.id === fobNumber);
  }
  return foundFob;  
}
//Returns only fobs which have matching stores
export function anyMatch<F extends ManageAPI.Fob | Grouping>(against: readonly ManageAPI.Store[]): (f: F) => boolean {
  return (f: F) => against.some((s) => {
    const ids = [s.fieldOfBusinessId].concat(s.additionalFieldOfBusinessIds || []);
    if ("id" in f) return ids.includes(f.id);
    return f.ids.some((id) => ids.includes(id));
  });
}
/**
 * 
 * @param stores Filters stores and returns a new array.
 * @param term search term 
 * @param by_floor limit to floor
 * @param by_fob limit to field of business
 * @returns 
 */
export function filterStores(
  stores: readonly ManageAPI.Store[], 
  term: string, 
  by_floor: string, 
  by_fob: string | Grouping
): ManageAPI.Store[] {

  term = term.toLowerCase();
  const floor_id = parseInt(by_floor);
  const fob_type = typeof by_fob;
  const fob_id = fob_type == 'number'  ? by_fob : 
  fob_type == 'string' && by_fob ? parseInt(by_fob) : undefined;

  if (!fob_id && fob_type !== 'string') {
    stores = filterByGrouping(stores, by_fob);
  }
  return stores.reduce((acc: ManageAPI.Store[], store) => {
      const _name = store.name.toLowerCase();
      const _floors = [store.floor?.id, store.secondaryFloor?.id];
      const _fob = store.fieldOfBusinessId;
      if (term && !_name.includes(term)) return acc;
      if (by_floor && !_floors.includes(floor_id)) return acc;
      if (fob_id && by_fob && _fob != fob_id) return acc;

      acc.push(store);

      return acc;
  }, []);
}

export type NormalizedURL = { href: string; text: string; };
export type Protocol = 'http' | 'https' | 'email' | 'tel';

export function normalizeURL(input: string, protocol: Protocol): NormalizedURL {
  
  if (!input) return { href: '', text: '' };
  const text = input = input.trim().replace(PROTO_REGEX, '');

  if (protocol == 'tel') {
    input = encodeURIComponent(input.replace(/\s/g, ''));
  }

  const prefix = protocol.startsWith('http') ? protocol + "://" : protocol + ':';
  let href = prefix + input;

  return {
    href,
    text,
  }
}